/* Admin Center — Subscription tab (extracted from AdminPage.tsx) */

import { useState, useEffect } from 'react';
import { rem } from '../../lib/rem';
import { Switch } from '../../components/ui/switch';
import { MODULE_DEFS, type AppModule } from '../../../shared/modules';
import { hasPermission } from '../../../shared/permissions';
import * as cloudCache from '../../services/cloudCache';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import { RowSkeleton } from './shared';

/* ═══════════ SUBSCRIPTION TAB ═══════════
   Reads + writes the `firm_modules` table directly via cloudCache. Each
   row is { id, firmId, module, enabled }. Toggling re-uses the existing
   row's id when one exists (so we update instead of accumulating dupes);
   first-time enables create a new row.
   The toggle is gated by `admin_center.manage_subscription` — users
   without it see the same cards as read-only badges.
   ═══════════════════════════════════════════════════════════════ */
interface FirmModuleRow {
  id: string;
  firmId?: string;
  module: AppModule;
  enabled: boolean;
}

function SubscriptionTab({
  toast,
  userPermissions,
}: {
  toast: AdminPageProps['toast'];
  userPermissions?: string[];
}) {
  const [rows, setRows] = useState<FirmModuleRow[]>([]);
  const [savingModule, setSavingModule] = useState<AppModule | null>(null);
  const [loading, setLoading] = useState(true);
  const canEdit = hasPermission(userPermissions, 'admin_center.manage_subscription');

  async function load() {
    try {
      const list = await cloudCache.list<FirmModuleRow>('firm_modules');
      setRows(list);
    } catch (err: any) {
      console.error('[SubscriptionTab] load failed:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);
  useTableSync(['firm_modules'], () => load());

  async function toggle(mod: AppModule, nextEnabled: boolean) {
    if (!canEdit || savingModule) return;
    setSavingModule(mod);
    try {
      const existing = rows.find((r) => r.module === mod);
      const record: FirmModuleRow = existing
        ? { ...existing, enabled: nextEnabled }
        : { id: '', module: mod, enabled: nextEnabled };
      const saved = await cloudCache.save('firm_modules', record);
      // Optimistic refresh — cloudCache already updated its cache, but pull
      // local state so the row id from a fresh insert is captured.
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.module === mod);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [...prev, saved];
      });
      toast(`${MODULE_DEFS[mod].name} ${nextEnabled ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      toast('Update failed: ' + (err?.message || String(err)), true);
      await load();
    } finally {
      setSavingModule(null);
    }
  }

  const allModules = Object.entries(MODULE_DEFS).filter(([id]) => id !== 'core') as [
    AppModule,
    (typeof MODULE_DEFS)[AppModule],
  ][];

  return (
    <div style={{ maxWidth: rem(720) }}>
      <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0 }}>Subscription</h3>
      <p
        style={{
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
          color: 'var(--tm)',
          margin: 'var(--space-1) 0 var(--space-4)',
        }}
      >
        {canEdit
          ? 'Toggle which modules your firm has access to. Disabling a module hides its pages and blocks its API endpoints for everyone in the firm.'
          : 'Modules currently enabled for your firm. Ask an administrator to change your subscription.'}
      </p>

      {loading ? (
        <RowSkeleton rows={3} label="Loading subscription modules" />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          {allModules.map(([id, def]) => {
            const row = rows.find((r) => r.module === id);
            const enabled = !!row?.enabled;
            const isSaving = savingModule === id;
            return (
              <div
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(14),
                  padding: `${rem(14)} var(--space-4)`,
                  background: 'var(--sf)',
                  borderRadius: 'var(--radius-lg)',
                  border: `1px solid ${enabled ? 'var(--ac)' : 'var(--bd)'}`,
                  transition: 'border-color .15s, background .15s',
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                <div
                  style={{
                    width: rem(36),
                    height: rem(36),
                    borderRadius: rem(8),
                    background: enabled ? 'var(--ac)' : 'var(--sa)',
                    color: enabled ? 'var(--color-text-inverse)' : 'var(--tm)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 700,
                    flexShrink: 0,
                    transition: 'background .15s, color .15s',
                  }}
                >
                  {def.name.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'calc(var(--font-size-base) * 14 / 13)',
                      fontWeight: 700,
                      color: 'var(--tx)',
                      marginBottom: rem(2),
                    }}
                  >
                    {def.name}
                  </div>
                  <div
                    style={{
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      color: 'var(--tm)',
                      lineHeight: 1.4,
                    }}
                  >
                    {def.description}
                  </div>
                </div>
                {canEdit ? (
                  <Switch
                    checked={enabled}
                    disabled={isSaving}
                    onCheckedChange={(next) => toggle(id, next)}
                    aria-label={`${enabled ? 'Disable' : 'Enable'} ${def.name}`}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '.05em',
                      padding: `${rem(4)} ${rem(10)}`,
                      borderRadius: 'var(--radius-full)',
                      background: enabled ? 'rgba(var(--okr),0.12)' : 'var(--sa)',
                      color: enabled ? 'var(--ok)' : 'var(--tm)',
                    }}
                  >
                    {enabled ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
            marginTop: 'var(--space-4)',
            padding: `${rem(10)} var(--space-3)`,
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            lineHeight: 1.5,
          }}
        >
          <strong>Note:</strong> Module changes take effect on next sign-in for other users in your
          firm. Core (Dashboard, Settings, Admin) is always enabled and cannot be turned off.
        </p>
      )}
    </div>
  );
}

export { SubscriptionTab };
